import React, { createContext, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

interface TitleContextValue {
  setPageTitle: (title: string) => void;
  pageTitle: string;
  partialTitle: string;
}

const TitleContext = createContext<TitleContextValue>({
  setPageTitle: () => {},
  pageTitle: '',
  partialTitle: ''
});

export const TitleProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation('common');

  const [pageTitle, setPageTitle] = useState(t('title.suffix'));

  const [partialTitle, setPartialTitle] = useState('');

  const updatePageTitle = (title: string) => {
    const newFullTitle = title ? t('title.template', { title }) : t('title.suffix');
    setPageTitle(newFullTitle);
    setPartialTitle(title);
  };

  const value: TitleContextValue = { partialTitle, pageTitle, setPageTitle: updatePageTitle };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <TitleContext.Provider value={value}>{children}</TitleContext.Provider>
    </>
  );
};

export function usePageTitle(newTitle?: string) {
  const hasTitleSpecified = arguments.length >= 0;
  const context = useContext(TitleContext);
  const [originalTitle] = useState(() => context.partialTitle);

  useEffect(() => {
    if (hasTitleSpecified) {
      context.setPageTitle(newTitle || '');
    }
  }, [context, hasTitleSpecified, newTitle]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => context.setPageTitle(originalTitle), []);

  return context;
}
